import { useMutation, useQueryClient } from "react-query";

import { duplicateTarget } from "api/targets";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_TARGETS } from "@constants/reactQueryKeys";

interface DuplicateTargetPayload {
  targetId: number;
  /** Parent goal id — required for invalidating the target list query. */
  goalId: number;
}

/**
 * Duplicate a target (silent action — no confirmation dialog). Copies the
 * full configuration, resets status to `not-active`, and clears session data
 * server-side. Invalidates the target list for the parent goal so the new
 * row appears immediately.
 */
const useDuplicateTarget = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: duplicateTargetAction,
    isLoading: isDuplicatingTarget,
    ...rest
  } = useMutation(({ targetId }: DuplicateTargetPayload) => duplicateTarget(targetId), {
    onSuccess: (response, variables) => {
      showToast({ message: "Target duplicated" });
      queryClient.invalidateQueries([GET_LIST_OF_TARGETS, variables.goalId]);
      queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);
      onSuccess(response, variables);
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || "Failed to duplicate target";
      showToast({ message: errorMessage, errorState: true });
      onError(error);
    },
    ...otherOptions,
  });

  return { duplicateTargetAction, isDuplicatingTarget, ...rest };
};

export default useDuplicateTarget;
